import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "../components/Header";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import "./RootLayout.css";
import Footer from "./Footer";
import { useAuth0 } from "@auth0/auth0-react";
import useStore from "../stores/useStore";
import { useNavigate } from "react-router-dom";
import LoaderVWC from "../components/LoaderVWC";
import useHttp from "../hooks/use-http";
import EersteKeerModal from "../modals/EersteKeerModal";
import { useTranslation } from "react-i18next";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RootLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithRedirect, isLoading } = useAuth0();
  const { redirectUri, setRedirectUri } = useStore();
  const { user } = useStore();
  const { i18n } = useTranslation();

  const [isStuck, setIsStuck] = useState(false);

  function resultsView() {
    return location.pathname.includes("results");
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 70) {
        setIsStuck(true);
      } else {
        setIsStuck(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setRedirectUri(location.pathname + location.search + location.hash);
      if (isAuthenticated !== true) {
        loginWithRedirect();
      }
    }
  }, [
    isAuthenticated,
    loginWithRedirect,
    location.pathname,
    location.search,
    location.hash,
    setRedirectUri,
    isLoading,
    navigate,
  ]);


  useEffect(() => {
    // Check if user just verified their email
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const message = urlParams.get('message');
  
    if (success === 'true' && message?.includes('verified') && !isAuthenticated) {
      // Email was verified, redirect to login to get new token
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    }
  }, [loginWithRedirect, isAuthenticated]);


  // Media queries
  const mobile = useMediaQuery(theme.breakpoints.between("mobile", "ipad"));
  const tablet = useMediaQuery(theme.breakpoints.between("ipad", "laptop"));
  const laptop = useMediaQuery(theme.breakpoints.up("laptop"));

  const { languageSaved, setLanguageSaved, setCheckCount } = useStore();

  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);

  const {
    sendRequest: checkLanguageSetRequest,
    isLoading: checkLanguageSetLoading,
    error: checkLanguageSetError,
  } = useHttp();

  function checkLanguageSet() {
    function applyData(res) {
      setLanguageSaved(res?.preferred_language_set || false);
      setCheckCount(res?.check_count || 0);
      i18n.changeLanguage(res?.preferred_language || "nl");

      if (res.preferred_language_set === false) {
        setShowFirstTimeModal(true);
      }
    }

    checkLanguageSetRequest(
      {
        url: `${REACT_APP_BACKEND_URL}/user_management/get/${user.sub}`,
      },
      applyData
    );
  }
  useEffect(() => {
    checkLanguageSet();
  }, []);

  return (
    <React.Fragment>
      {isAuthenticated !== true ? (
        <LoaderVWC compact />
      ) : (
        <React.Fragment>
          {!!showFirstTimeModal && (
            <EersteKeerModal
              languageSaved={languageSaved}
              setLanguageSaved={setLanguageSaved}
            />
          )}

          {/* MOBILE */}
          {mobile && (
            <div>
              {/* Header */}
              <Header />

              {/* Outlet */}
              <div id="m-main">
                <div className="m-main-content">
                  <Outlet />
                </div>
              </div>
              {/* Footer */}
              <div id="m-footer">
                <Footer />
              </div>
            </div>
          )}

          {/* TABLET */}
          {tablet && (
            <div>
              {/* Header */}
              <Header />

              {/* Outlet */}
              <div id="t-main">
                <div className="t-main-content">
                  <Outlet />
                </div>
              </div>

              {/* Footer */}
              <div id="t-footer">
                <Footer />
              </div>
            </div>
          )}

          {/* LAPTOP */}
          {laptop && (
            <div id="d-container">
              {/* Header */}
              <header
                className={`d-header has-sticky sticky-jump  `}
                style={{
                  height: isStuck ? "95px" : "",
                }}
              >
                <div
                  id="d-header-wrapper"
                  className={`d-header-wrapper ${isStuck ? "stuck" : ""}
                  ${resultsView() ? "results-header" : ""}
                  `}
                >
                  <Header />
                </div>
              </header>

              {/* Outlet */}
              <div id="d-main">
                <div
                  className={`d-main-content ${
                    resultsView() ? "results-page" : ""
                  }`}
                >
                  <Outlet />
                </div>
              </div>

              {/* Footer */}
              <div
                id="d-footer"
                className={`${resultsView() ? "results-footer" : ""}`}
              >
                <Footer />
              </div>
            </div>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
export default RootLayout;
